import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";

import { isApiTransportError } from "../api/errors";
import { PwaContext, type PwaServices } from "./pwaServices";

/**
 * Запускает слежение за сетью, установкой и вводом в формах, а в собранном
 * приложении — и service worker. В dev-режиме регистрировать его не нужно:
 * он кеширует собранные файлы, которых у dev-сервера нет.
 */
export function PwaProvider({
  services,
  registerWorker = import.meta.env.PROD,
  children,
}: {
  services: PwaServices;
  registerWorker?: boolean;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const stops = [
      services.connection.start(),
      services.installer.start(),
      services.unsaved.attach(),
      // Запись, которая не дошла до сервера, не должна пройти молча: без
      // этого форма просто показала бы общую ошибку без объяснения причины.
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "error" &&
          isApiTransportError(event.action.error)
        ) {
          services.connection.reportRejectedWrite();
        }
      }),
    ];
    let cancelled = false;
    let stopUpdater: (() => void) | undefined;
    if (registerWorker) {
      void services.updater.start().then((stop) => {
        if (cancelled) stop();
        else stopUpdater = stop;
      });
    }

    return () => {
      cancelled = true;
      stopUpdater?.();
      for (const stop of stops) stop();
    };
  }, [services, registerWorker, queryClient]);

  return <PwaContext.Provider value={services}>{children}</PwaContext.Provider>;
}
